/**
 * resolveTokenColor - 解析 Antd Design Token 颜色
 * 支持输入 token 名称（如 colorPrimary）或直接颜色值（如 #1890ff）
 */

import { theme } from "antd";

// 常用 Design Token 映射
const TOKEN_MAP: Record<string, string> = {
  // 基础颜色
  colorPrimary: "colorPrimary",
  colorSuccess: "colorSuccess",
  colorWarning: "colorWarning",
  colorError: "colorError",
  colorInfo: "colorInfo",

  // 文本颜色
  colorText: "colorText",
  colorTextSecondary: "colorTextSecondary",
  colorTextTertiary: "colorTextTertiary",
  colorTextQuaternary: "colorTextQuaternary",
  colorTextBase: "colorTextBase",

  // 背景颜色
  colorBgBase: "colorBgBase",
  colorBgContainer: "colorBgContainer",
  colorBgElevated: "colorBgElevated",
  colorBgLayout: "colorBgLayout",
  colorBgSpotlight: "colorBgSpotlight",

  // 边框颜色
  colorBorder: "colorBorder",
  colorBorderSecondary: "colorBorderSecondary",

  // 填充颜色
  colorFill: "colorFill",
  colorFillSecondary: "colorFillSecondary",
  colorFillTertiary: "colorFillTertiary",
  colorFillQuaternary: "colorFillQuaternary",

  // 链接颜色
  colorLink: "colorLink",
  colorLinkHover: "colorLinkHover",
  colorLinkActive: "colorLinkActive",
};

// 导出可用的 token 列表
export const AVAILABLE_TOKENS = Object.keys(TOKEN_MAP);

// 常用 token 快捷选项
export const COMMON_TOKENS = [
  { label: "主色", value: "colorPrimary" },
  { label: "成功", value: "colorSuccess" },
  { label: "警告", value: "colorWarning" },
  { label: "错误", value: "colorError" },
  { label: "信息", value: "colorInfo" },
  { label: "文本", value: "colorText" },
  { label: "次级文本", value: "colorTextSecondary" },
  { label: "链接", value: "colorLink" },
];

/**
 * 判断是否为 token 名称
 */
export function isTokenName(value: string): boolean {
  return TOKEN_MAP.hasOwnProperty(value);
}

/**
 * 使用 Antd theme hook 解析 token 颜色
 * 注意：这是一个 React Hook，必须在组件内部使用
 */
export function useResolveTokenColor(
  color: string | undefined
): string | undefined {
  const { token } = theme.useToken();

  if (!color) return undefined;

  // 检查是否为 token 名称
  if (isTokenName(color)) {
    const tokenKey = TOKEN_MAP[color];
    return (token as any)[tokenKey] || color;
  }

  // 直接返回颜色值
  return color;
}

/**
 * 非 Hook 版本：需要传入 token 对象
 */
export function resolveTokenColorWithToken(
  color: string | undefined,
  token: Record<string, any>
): string | undefined {
  if (!color) return undefined;

  // 检查是否为 token 名称
  if (isTokenName(color)) {
    const tokenKey = TOKEN_MAP[color];
    return token[tokenKey] || color;
  }

  // 直接返回颜色值
  return color;
}

export default useResolveTokenColor;
